import React, { useCallback, useEffect, useState } from 'react';
import { getTodayDate } from '../../utils/dateUtils';
import axios from 'axios';

import { DateField } from '../shared/DateField';

/**
 * Repo Lifecycle — post-ACQUIRE financing-book management (repo E2E
 * F-REPO-010, 2026-08-27: accrue / re-mark / close / risk were API-only with
 * no UI entry point; the ticket's Reverse button is refused by design for
 * repos, so this panel is the operational surface).
 *
 * Lists OPEN repo contracts for a portfolio from
 * GET /api/derivatives/repo/risk/{portfolioId} (which also serves the
 * aggregate financing strip), with per-row actions:
 *   Accrue  -> POST /api/derivatives/repo/accrue/{positionId}?date=
 *   Re-mark -> POST /api/derivatives/repo/remark/{positionId}?dirtyPrice=&date=
 *   Close   -> POST /api/derivatives/repo/close/{positionId}?date=
 * Every action books a REAL posted lifecycle trade through RepoPostingSpi
 * (bi-temporal supersede; closing cash settles on close).
 */

const panel: React.CSSProperties = {
  border: '1px solid rgba(148,163,184,0.18)', background: 'rgba(15,23,42,0.55)',
  borderRadius: 10, padding: '16px 20px', marginBottom: 16,
};
const label: React.CSSProperties = {
  color: '#64748b', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
};
const th: React.CSSProperties = {
  ...label, textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid rgba(148,163,184,0.18)',
};
const td: React.CSSProperties = {
  color: '#e2e8f0', fontSize: 13, textAlign: 'right', padding: '8px 10px',
  borderBottom: '1px solid rgba(255,255,255,0.04)', whiteSpace: 'nowrap',
};
const input: React.CSSProperties = {
  background: '#0f172a', border: '1px solid rgba(148,163,184,0.3)', borderRadius: 8,
  color: '#e2e8f0', padding: '8px 12px', fontSize: 13, width: '100%',
};
const btn = (tone: string): React.CSSProperties => ({
  background: tone === 'amber' ? 'rgba(245,158,11,0.15)' : tone === 'red' ? 'rgba(239,68,68,0.15)' : 'rgba(59,130,246,0.15)',
  border: `1px solid ${tone === 'amber' ? 'rgba(245,158,11,0.4)' : tone === 'red' ? 'rgba(239,68,68,0.4)' : 'rgba(59,130,246,0.4)'}`,
  color: tone === 'amber' ? '#fbbf24' : tone === 'red' ? '#f87171' : '#60a5fa',
  borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginLeft: 6,
});

const fmt = (v: any, digits = 2) =>
  v == null ? '—' : Number(v).toLocaleString(undefined, { maximumFractionDigits: digits });

const todayIso = () => getTodayDate();

type RepoRow = {
  positionId: number;
  contractId: number;
  repoType: string;
  direction: string;
  counterparty: string | null;
  collateralCusip: string | null;
  collateralDescription: string | null;
  collateralDirtyPrice: number | null;
  openingCash: number | null;
  collateralMarketValue: number | null;
  repoRatePct: number | null;
  startDate: string | null;
  endDate: string | null;
  daysToMaturity: number | null;
  accruedInterest: number | null;
  lastAccrualDate: string | null;
  marginStatus: string | null;
  lifecycleStatus: string | null;
};

type ModalState = { kind: 'accrue' | 'remark' | 'close'; row: RepoRow } | null;

const RepoLifecycleScreen: React.FC = () => {
  const [portfolios, setPortfolios] = useState<any[]>([]);
  const [portfolioId, setPortfolioId] = useState<string>('');
  const [risk, setRisk] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState<ModalState>(null);
  const [dateVal, setDateVal] = useState(todayIso());
  const [priceVal, setPriceVal] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    axios.get('/api/dimensions/portfolios')
      .then(r => setPortfolios(Array.isArray(r.data) ? r.data : (r.data?.content || [])))
      .catch(() => setPortfolios([]));
  }, []);

  const load = useCallback(async (pid: string) => {
    if (!pid) { setRisk(null); return; }
    setBusy(true);
    try {
      const { data } = await axios.get(`/api/derivatives/repo/risk/${pid}`);
      setRisk(data);
    } catch { setRisk(null); } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(portfolioId); }, [portfolioId, load]);

  const openModal = (kind: 'accrue' | 'remark' | 'close', row: RepoRow) => {
    setModal({ kind, row });
    setDateVal(todayIso());
    setPriceVal(row.collateralDirtyPrice != null ? String(row.collateralDirtyPrice) : '');
    setMessage(null);
  };

  const runAction = async () => {
    if (!modal) return;
    const { kind, row } = modal;
    setBusy(true);
    setMessage(null);
    try {
      let res;
      if (kind === 'accrue') {
        res = await axios.post(`/api/derivatives/repo/accrue/${row.positionId}?date=${dateVal}`);
      } else if (kind === 'remark') {
        res = await axios.post(`/api/derivatives/repo/remark/${row.positionId}?dirtyPrice=${encodeURIComponent(priceVal)}&date=${dateVal}`);
      } else {
        res = await axios.post(`/api/derivatives/repo/close/${row.positionId}?date=${dateVal}`);
      }
      setMessage(res.data?.message || 'Done');
      setModal(null);
      await load(portfolioId);
    } catch (e: any) {
      setMessage(`Failed: ${e.response?.data?.message || e.response?.data?.error || e.message}`);
    } finally { setBusy(false); }
  };

  const rows: RepoRow[] = risk?.contracts || [];

  return (
    <div style={panel}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ color: '#f8fafc', fontSize: 15, fontWeight: 800 }}>Repo Lifecycle</div>
        <div style={{ color: '#64748b', fontSize: 12 }}>
          Open financing book — accrue interest, re-mark collateral, close at maturity. Every action posts a real lifecycle trade.
        </div>
      </div>

      {/* Left-aligned portfolio block — parity with the Options/Swaps panels
          (operator finding 2026-08-27). */}
      <div style={{ ...panel, padding: '12px 16px' }}>
        <div style={label}>Portfolio</div>
        <select style={{ ...input, maxWidth: 420, marginTop: 6 }} value={portfolioId}
          onChange={e => setPortfolioId(e.target.value)}>
          <option value="">Select portfolio…</option>
          {portfolios.map((p: any) => <option key={p.id} value={p.id}>{p.portfolioName}</option>)}
        </select>
      </div>

      {message && (
        <div style={{ ...panel, padding: '10px 14px', color: message.startsWith('Failed') ? '#f87171' : '#34d399', fontSize: 13 }}>
          {message}
        </div>
      )}

      {risk && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
          <Stat label="Open Repos" value={fmt(risk.openRepoPositions, 0)} />
          <Stat label="Cash Borrowed" value={`$${fmt(risk.cashBorrowedRepo)}`} />
          <Stat label="Cash Lent" value={`$${fmt(risk.cashLentReverseRepo)}`} />
          <Stat label="Net Financing" value={`$${fmt(risk.netCashFinancing)}`} />
          <Stat label="Collateral Pledged" value={`$${fmt(risk.collateralPledged)}`} />
          <Stat label="Haircut Cushion" value={`$${fmt(risk.haircutCushion)}`} />
          <Stat label="Accrued Interest" value={`$${fmt(risk.accruedInterest)}`} />
          <Stat label="W.Avg Rate" value={risk.weightedAvgRepoRatePct != null ? `${fmt(risk.weightedAvgRepoRatePct, 4)}%` : '—'} />
          <Stat label="W.Avg Maturity" value={risk.weightedAvgMaturityDays != null ? `${fmt(risk.weightedAvgMaturityDays)}d` : '—'} />
          <Stat label="Margin Calls" value={fmt(risk.openMarginCalls, 0)} tone={Number(risk.openMarginCalls) > 0 ? 'red' : undefined} />
        </div>
      )}

      {portfolioId && !busy && rows.length === 0 && (
        <div style={{ color: '#64748b', fontSize: 13, padding: '10px 0' }}>No open repo contracts in this portfolio.</div>
      )}

      {rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left' }}>Contract</th>
                <th style={{ ...th, textAlign: 'left' }}>Collateral</th>
                <th style={th}>Opening Cash</th>
                <th style={th}>Rate %</th>
                <th style={th}>Accrued</th>
                <th style={th}>Last Accrual</th>
                <th style={th}>Maturity</th>
                <th style={th}>Margin</th>
                <th style={{ ...th, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.positionId}>
                  <td style={{ ...td, textAlign: 'left' }}>
                    <span style={{ color: '#67e8f9', fontWeight: 700 }}>#{r.contractId}</span>{' '}
                    {r.repoType} <span style={{ color: '#64748b' }}>({r.direction})</span>
                    {r.counterparty && <span style={{ color: '#94a3b8' }}> · {r.counterparty}</span>}
                  </td>
                  <td style={{ ...td, textAlign: 'left' }}>
                    {r.collateralCusip || '—'}
                    {r.collateralDirtyPrice != null && <span style={{ color: '#64748b' }}> @ {fmt(r.collateralDirtyPrice, 4)}</span>}
                  </td>
                  <td style={td}>${fmt(r.openingCash)}</td>
                  <td style={td}>{fmt(r.repoRatePct, 4)}</td>
                  <td style={td}>${fmt(r.accruedInterest)}</td>
                  <td style={td}>{r.lastAccrualDate || '—'}</td>
                  <td style={td}>{r.endDate || 'open'}{r.daysToMaturity != null ? ` (${r.daysToMaturity}d)` : ''}</td>
                  <td style={{ ...td, color: r.marginStatus === 'CALL' ? '#f87171' : '#34d399', fontWeight: 700 }}>
                    {r.marginStatus || '—'}
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <button style={btn('blue')} onClick={() => openModal('accrue', r)}>Accrue</button>
                    <button style={btn('amber')} onClick={() => openModal('remark', r)}>Re-mark</button>
                    <button style={btn('red')} onClick={() => openModal('close', r)}>Close</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.7)', zIndex: 60,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ ...panel, width: 420, background: '#0b1220' }}>
            <div style={{ color: '#f8fafc', fontWeight: 800, marginBottom: 4 }}>
              {modal.kind === 'accrue' && `Accrue repo #${modal.row.contractId}`}
              {modal.kind === 'remark' && `Re-mark collateral — repo #${modal.row.contractId}`}
              {modal.kind === 'close' && `Close repo #${modal.row.contractId}`}
            </div>
            <div style={{ color: '#64748b', fontSize: 12, marginBottom: 12 }}>
              {modal.kind === 'accrue' && 'Accrues daily interest into the carrying value up to the date (capped at maturity).'}
              {modal.kind === 'remark' && 'Re-marks the collateral dirty price and re-runs the margin test (financing leg unchanged).'}
              {modal.kind === 'close' && 'Settles the repurchase cash and realizes the interest. Before maturity this is an early payoff at interest accrued to date.'}
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={label}>As-of date</div>
              <DateField style={{ width: '100%' }} value={dateVal ?? ''} onChange={iso => setDateVal(iso)} />
            </div>
            {modal.kind === 'remark' && (
              <div style={{ marginBottom: 10 }}>
                <div style={label}>New dirty price (% of par)</div>
                <input style={input} type="number" value={priceVal} onChange={e => setPriceVal(e.target.value)} />
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button style={btn('blue')} disabled={busy} onClick={() => setModal(null)}>Cancel</button>
              <button style={btn(modal.kind === 'close' ? 'red' : 'amber')} disabled={busy || (modal.kind === 'remark' && !priceVal)} onClick={runAction}>
                {busy ? 'Working…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string; tone?: 'red' }> = ({ label: l, value, tone }) => (
  <div style={{ background: 'rgba(2,6,23,0.35)', border: '1px solid rgba(71,85,105,0.38)', borderRadius: 11, padding: '10px 12px' }}>
    <div style={label}>{l}</div>
    <div style={{ color: tone === 'red' ? '#f87171' : '#f8fafc', fontSize: 15, fontWeight: 800 }}>{value}</div>
  </div>
);

export default RepoLifecycleScreen;
